import { useQuery } from '@tanstack/react-query'
import {
  Chip,
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton, Stack,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  open: boolean
  warningDays?: number
  onClose(): void
}

/**
 * K19 (dependents approaching maioridade) drill-down dialog. Pattern B — full PII.
 * Reads with workspace-specific max_age resolved by the backend.
 */
export function MaioridadeDrilldownDialog({ workspaceId, open, warningDays, onClose }: IProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'maioridade-drilldown', workspaceId, warningDays],
    queryFn: () => governanceApi.maioridadeDrilldown({ workspaceId, warningDays }),
    enabled: open && !!workspaceId,
  })

  const rows = data?.data.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" component="div">Dependentes próximos da maioridade (K19)</Typography>
          {data && <Chip size="small" label={`max_age ${data.data.max_age} anos`} />}
          {data && <Chip size="small" label={`janela ${data.data.warning_days}d`} variant="outlined" />}
        </Stack>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar dependentes.</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dependente (CPF)</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Titular</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Nascimento</TableCell>
                <TableCell align="right">Idade</TableCell>
                <TableCell align="right">Dias até max</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.person_tax_payer_id}>
                  <TableCell>{r.person_tax_payer_id}</TableCell>
                  <TableCell>{r.person_name ?? '—'}</TableCell>
                  <TableCell>{r.titular_name ?? '—'}</TableCell>
                  <TableCell>{r.dependent_type_id ?? '—'}</TableCell>
                  <TableCell>{r.birth_date ? dayjs(r.birth_date).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell align="right">{r.age_years}</TableCell>
                  <TableCell align="right">{r.days_to_max_age}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">Nenhum dependente na janela.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
